import { handleError } from '@/general/errorHandling'
import { COMFORTAI, IND, INDAI } from '@/general/master-data'
import { IPaxConfig } from '@/store/catalog/interfaces'
import { usePriceStore } from '.'
import { useDetailsStore, useFlightStore, useRailStore } from '..'
import { useFlexFlightStore } from '../flex-flight'
import { IError, IFetchRailOption, RailDirection } from '../rail/interfaces'
import { priceHelper } from './helper'
import { IService } from './interfaces'

export const reset = () => {
  const priceStore = usePriceStore()

  priceStore.cheapestFlightPrices = null
  priceStore.extras = null
}

export const getFlightServices = (paxIds: number[], paxConfig: IPaxConfig): IService[] => {
  const flightStore = useFlightStore()
  const detailsStore = useDetailsStore()

  try {
    const flightServices = priceHelper.createFlightServices(paxIds, paxConfig, flightStore.selectedFlight)
    const rnfService = getRNFService(
      flightStore.selectedRailOption,
      flightStore.railDirectionRNF,
      paxIds,
      paxConfig
    )

    return !!rnfService ? flightServices.concat(rnfService) : flightServices
  } catch (error) {
    handleError({ error, origin: 'getFlightServices' })
    detailsStore.failedServiceForBooking = 'Flug'
    return []
  }
}

export const getFlexFlightServices = (paxIds: number[], paxConfig: IPaxConfig): IService[] => {
  const flexFlightStore = useFlexFlightStore()
  const detailsStore = useDetailsStore()

  try {
    const flexFightServices = priceHelper.createFlexFlightServices(paxIds, paxConfig, flexFlightStore.selectedFlight)
    const rnfService = getRNFService(
      flexFlightStore.selectedRailOption,
      flexFlightStore.railDirection,
      paxIds,
      paxConfig
    )

    return !!rnfService ? flexFightServices.concat(rnfService) : flexFightServices
  } catch (error) {
    handleError({ error, origin: 'getFlexFlightServices' })
    detailsStore.failedServiceForBooking = 'Wunschflug'
    return []
  }
}

export const getRNFService = (
  RNFOption: IFetchRailOption | IError | null,
  railDirection: RailDirection,
  paxIds: number[],
  paxConfig: IPaxConfig
): IService | null => {
  const detailsStore = useDetailsStore()

  const priceModel = detailsStore.priceModel!
  const isPremium = [IND, INDAI].includes(priceModel)
  const isComfort = [COMFORTAI].includes(priceModel)
  const hasRNFClassSelected = !!RNFOption && 'railClassCode' in RNFOption
  const isComfortAIAndRNFFirstClass = isComfort && hasRNFClassSelected && RNFOption.railClassCode === '1'
  const isPremiumAndRNFSecondClass = isPremium && hasRNFClassSelected && RNFOption.railClassCode === '2'

  if (!hasRNFClassSelected || !!isPremiumAndRNFSecondClass || !!isComfortAIAndRNFFirstClass) return null

  try {
    return priceHelper.createRailService(paxIds, paxConfig, railDirection, RNFOption as IFetchRailOption)
  } catch (error) {
    handleError({ error, origin: 'getRNFService' })
    detailsStore.failedServiceForBooking = 'RNF'
    return null
  }
}

export const getRITService = (paxIds: number[], paxConfig: IPaxConfig): IService | null => {
  const railStore = useRailStore()
  const detailsStore = useDetailsStore()

  try {
    return priceHelper.createRailService(
      paxIds,
      paxConfig,
      railStore.railDirection,
      railStore.selectedRailOption as IFetchRailOption
    )
  } catch (error) {
    handleError({ error, origin: 'getRITService' })
    detailsStore.failedServiceForBooking = 'RIT'
    return null
  }
}

export default {
  getFlightServices,
  getFlexFlightServices,
  getRITService,
  getRNFService,
}

export const exportedForTesting = {
  reset
}
